// src/modules/leads/components/LeadDetailPanel.jsx
//
// The lead detail view opened by "Open".
//
// On desktop it is a centered modal, not a right-hand drawer: a 620px slide-in
// squeezed the log-a-call form and the two history lists into a column while
// leaving most of the screen unused. Centred, the same content gets the width
// it needs and the workspace stays visibly underneath.
//
// On a phone it stays a bottom sheet — that is the shape a thumb expects, and a
// centred dialog on a 390px screen is just a modal with margins.
import React, { useEffect, useState } from 'react';
import {
  Drawer, Modal, Descriptions, Tag, Timeline, Form, Select, Input, DatePicker, Button,
  Space, Divider, Typography, message, Empty, Spin, Grid, Row, Col, Tabs,
} from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchLead, logLeadActivity, fetchLeadStatuses } from '../api';
import { STATUS_OPTIONS, OUTCOME_OPTIONS, tagFor } from '../constants';
import CallButton from '../../callSync/components/CallButton';
import LeadCallHistory from '../../callSync/components/LeadCallHistory';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export default function LeadDetailPanel({ leadId, open, onClose, onChanged }) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState([]);
  // Bumped after a call finishes so the Call History section refetches.
  const [callRefreshKey, setCallRefreshKey] = useState(0);
  const [form] = Form.useForm();

  // code → { name, color } lookup for the dynamic Status tag / dropdown.
  const statusMap = Object.fromEntries(leadStatuses.map((s) => [s.code, s]));

  useEffect(() => { fetchLeadStatuses().then((r) => setLeadStatuses(r?.data?.data || [])).catch(() => {}); }, []);

  const load = async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await fetchLead(leadId);
      setLead(res.data.data);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load lead');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && leadId) { load(); form.resetFields(); } /* eslint-disable-next-line */ }, [open, leadId]);

  const submitCall = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      await logLeadActivity(leadId, {
        type: 'CALL',
        outcome: v.outcome,
        remark: v.remark,
        interestLevel: v.interestLevel,
        nextCallDate: v.nextCallDate ? v.nextCallDate.toISOString() : undefined,
        visitDate: v.visitDate ? v.visitDate.toISOString() : undefined,
      });
      message.success('Call logged');
      form.resetFields();
      load();
      onChanged && onChanged();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed to log call');
    } finally { setSaving(false); }
  };

  const st = lead ? tagFor(STATUS_OPTIONS, lead.status) : null;

  const title = lead
    ? (
      <Space size={4} wrap>
        <span>{lead.name}</span>
        <Text code style={{ fontSize: 12 }}>{lead.reference}</Text>
      </Space>
    )
    : 'Lead';

  const body = (
    <Spin spinning={loading}>
      {!lead ? <Empty /> : (
        <>
          {/* Dials the customer from the rep's own Android handset via the
              Call Sync app. The desktop never talks to the phone directly —
              the backend is the authority. */}
          <Space wrap style={{ marginBottom: 12 }}>
            <Tag color={st.color}>{st.label}</Tag>
            <Tag icon={<PhoneOutlined />}>{lead.phone}</Tag>
            {lead.interestLevel && (
              <Tag color={statusMap[lead.interestLevel]?.color || 'default'}>{statusMap[lead.interestLevel]?.name || lead.interestLevel}</Tag>
            )}
            <CallButton
              lead={lead}
              size="small"
              onCallFinished={() => { setCallRefreshKey((k) => k + 1); load(); onChanged && onChanged(); }}
            />
          </Space>

          <Tabs
            defaultActiveKey="log"
            items={[
              {
                key: 'log',
                label: 'Log a Call',
                children: (
                  <div style={{ marginTop: 8 }}>
                    {lead.status !== 'CONVERTED' && (
                      <Form form={form} layout="vertical">
                        {/* A responsive grid, not a wrapping Space with fixed widths:
                            those overflow a phone instead of stacking. */}
                        <Row gutter={12}>
                          <Col xs={24} sm={12} md={6}>
                            <Form.Item name="outcome" label="Outcome" rules={[{ required: true }]}>
                              <Select options={OUTCOME_OPTIONS} placeholder="Call outcome" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={6}>
                            <Form.Item name="interestLevel" label="Status">
                              <Select allowClear placeholder="Status"
                                options={leadStatuses.map((s) => ({ value: s.code, label: <Tag color={s.color || 'default'}>{s.name}</Tag> }))} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={6}>
                            <Form.Item name="nextCallDate" label="Next Call Date">
                              <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={6}>
                            <Form.Item name="visitDate" label="Visit Date">
                              <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="remark" label="What the customer said">
                              <Input.TextArea rows={2} placeholder="Remark / notes" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Button type="primary" icon={<PhoneOutlined />} loading={saving}
                          onClick={submitCall} block={isMobile}>Save Call</Button>
                      </Form>
                    )}
                    <Divider orientation="left" style={{ marginTop: 24 }}>Call History</Divider>
                    <LeadCallHistory leadId={leadId} refreshKey={callRefreshKey} />
                  </div>
                ),
              },
              {
                key: 'details',
                label: 'Customer Details',
                children: (
                  <div style={{ marginTop: 8 }}>
                    {/* Two columns on desktop: at the modal's width a single column
                        leaves a bordered label beside a mostly-empty cell. */}
                    <Descriptions size="small" column={isMobile ? 1 : 2} bordered
                      labelStyle={isMobile ? { width: 110, padding: '6px 8px' } : { width: 120 }}
                      contentStyle={isMobile ? { padding: '6px 8px' } : undefined}
                    >
                      <Descriptions.Item label="Phone">
                        <a href={`tel:${lead.phone}`}>{lead.phone}</a>
                        {lead.altPhone ? <> / <a href={`tel:${lead.altPhone}`}>{lead.altPhone}</a></> : ''}
                      </Descriptions.Item>
                      <Descriptions.Item label="Email">{lead.email || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Address" span={isMobile ? 1 : 2}>{[lead.address, lead.city, lead.state, lead.pincode].filter(Boolean).join(', ') || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Lead Type">{lead.leadType || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Branch">{lead.branch?.name || '—'}</Descriptions.Item>
                      <Descriptions.Item label="Assigned To">{lead.assignedTo?.name || 'Unassigned'}</Descriptions.Item>
                      <Descriptions.Item label="Next Call">{lead.nextCallDate ? dayjs(lead.nextCallDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                      <Descriptions.Item label="Visit Date">{lead.visitDate ? dayjs(lead.visitDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                      <Descriptions.Item label="Call Attempts">{lead.callAttempts || 0}</Descriptions.Item>
                      <Descriptions.Item label="Last Remark" span={isMobile ? 1 : 2}>{lead.lastRemark || '—'}</Descriptions.Item>
                    </Descriptions>

                    <Divider orientation="left" style={{ marginTop: 24 }}>Activity History</Divider>
                    {(!lead.activities || !lead.activities.length) ? <Empty description="No activity yet" /> : (
                      <Timeline
                        items={lead.activities.map((a) => ({
                          color: a.outcome === 'CONVERTED' ? 'green' : a.outcome === 'NOT_INTERESTED' ? 'red' : 'blue',
                          children: (
                            <div>
                              <Text strong>{a.type}</Text>{a.outcome ? ` · ${tagFor(OUTCOME_OPTIONS, a.outcome).label}` : ''}
                              <div style={{ fontSize: 12, color: '#888' }}>
                                {dayjs(a.createdAt).format('DD/MM/YYYY HH:mm')} · {a.csr?.name || 'System'}
                                {a.statusFrom && a.statusTo && a.statusFrom !== a.statusTo ? ` · ${a.statusFrom} → ${a.statusTo}` : ''}
                              </div>
                              {a.remark && <div>{a.remark}</div>}
                              {a.nextCallDate && <div style={{ fontSize: 12 }}>Next call: {dayjs(a.nextCallDate).format('DD/MM/YYYY')}</div>}
                            </div>
                          ),
                        }))}
                      />
                    )}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </Spin>
  );

  // A phone keeps the bottom sheet; the thumb is at the bottom of the screen.
  if (isMobile) {
    return (
      <Drawer
        title={title}
        placement="bottom"
        width="100%"
        height="92%"
        open={open}
        onClose={onClose}
        destroyOnClose
        styles={{ body: { padding: 12 } }}
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      // No OK/Cancel: every action in here (Call, Save Call) commits
      // on its own, so a modal footer would be a button that does nothing.
      footer={null}
      centered
      width={880}
      styles={{
        // The two history lists can run long. Scroll the body rather than the
        // page, so the title and close button stay reachable.
        body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', paddingRight: 8 },
      }}
    >
      {body}
    </Modal>
  );
}
